// React Dependencies
import React, { useState, useReducer, useEffect } from 'react';

// External Dependencies
import Lightbox from 'react-image-lightbox';
import { IconButton, Tooltip } from '@material-ui/core';
import { Visibility as VisibilityIcon, CloudUpload as CloudUploadIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';
import 'react-image-lightbox/style.css';

// Internal Dependencies
import OverLoading from '../../../../../components/Loading';
import MText from '../../../../../components/Common/MText';
import Colours from '../../../../../styles/colours';
import BoundingBox from './BoundingBox';
import ReceiptField from './ReceiptField';
import { getImageSize } from '../../../../../common/util';
import { IsEmpty } from '../../../../../common/checks';

// Sample data
import { sampleImage, sampleExtraction } from './sample-data';

const SelectionMode = {
  Brand:         'brand',
  ReceiptNumber: 'receiptNo',
  DateTime:      'datetime',
  Amount:        'amount',
};

const defaultInputs = {
  [SelectionMode.Brand]:         [],
  [SelectionMode.ReceiptNumber]: [],
  [SelectionMode.DateTime]:      [],
  [SelectionMode.Amount]:        [],
};

const Receipt = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageSize, setImageSize] = useState(null);
  const [pointedText, setPointedText] = useState('');
  const [extractedData, setExtractedData] = useState([]);
  const [selectionMode, setSelectionMode] = useState(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  const [inputs, setInputs] = useReducer((state, action) => ({ ...state, ...action }), defaultInputs);

  const fetchReceiptDetails = async () => {
    try {
      setIsLoading(true);
      // setImageUrl(sampleImage);
      // setExtractedData(sampleExtraction);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = () => {
    try {
      setIsLoading(true);
      /**
       * Logic for uploading receipt image
       */
      setImageUrl(sampleImage);
      setExtractedData(sampleExtraction);
      toast.success('Receipt image uploaded successfully.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchImageSize = async () => {
    try {
      if (!IsEmpty(imageUrl)) {
        const { width, height } = await getImageSize(imageUrl);
        setImageSize({ width, height });
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePolygonClick = (line) => {
    if (inputs[selectionMode].includes(line)) {
      setInputs({ [selectionMode]: inputs[selectionMode].filter((l) => l !== line) });
    } else {
      setInputs({ [selectionMode]: [...inputs[selectionMode], line] });
    }
  };

  const formatLabeledText = (field) => (
    extractedData.filter((item) => inputs[field].includes(item.line)).map((item) => item.text).join(' ')
  );

  const handleSelectionModeChange = (e) => setSelectionMode(e.target.name === selectionMode ? null : e.target.name);

  useEffect(() => {
    fetchReceiptDetails();
  }, []);

  useEffect(() => {
    fetchImageSize();
  }, [imageUrl]);

  return (
    <div className="container-fluid pt-3">
      {isLoading && <OverLoading />}
      <div className="row">
        <div
          className="col-xl-9 col-12 pl-0 d-flex justify-content-center align-items-center px-3 py-2"
          style={{ borderRadius: '15px', backgroundColor: Colours.Gray5, border: `2px solid ${Colours.Black}` }}
        >
          <MText
            size={12}
            colour={Colours.Black}
            style={{ textAlign: 'center', fontWeight: 500 }}
            text={`
              Help the system recognise and understand your brand's receipt so it can efficiently capture the 
              necessary data and issue the correct number of stamps to users.
            `}
          />
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-12 pl-0">
          <MText
            size={12}
            colour={Colours.Black}
            style={{ textAlign: 'left', fontWeight: 500 }}
            text="A. Upload a sample of your brand's receipt"
          />
          <div className="d-flex flex-column pl-2">
            {
              [
                'Please ensure the following 4 items are clearly visible:',
                '1. Brand name and address',
                '2. Receipt/transaction number',
                '3. Date and time of transaction',
                '4. Net amount paid',
              ].map((text) => (
                <MText
                  key={text}
                  size={12}
                  colour={Colours.Gray7}
                  style={{ textAlign: 'left', fontWeight: 500 }}
                  text={text}
                />
              ))
            }
          </div>
        </div>
      </div>

      <div className="row mt-3 pl-0">

        {!IsEmpty(imageUrl) && !IsEmpty(extractedData) && (
          <div className="col-xl-4 col-12 d-flex flex-column justify-content-start pl-0 mb-4">
            <MText
              size={12}
              colour={Colours.Black}
              style={{ textAlign: 'left', fontWeight: 500 }}
              text="B. Verification"
            />
            <div className="d-flex flex-column pl-2">
              <MText
                size={12}
                colour={Colours.Gray7}
                className="mb-3"
                style={{ textAlign: 'left', fontWeight: 500 }}
                text="Click each item below, and select the respective text line on the uploaded receipt image"
              />

              <div className="container-fluid pl-2 pr-0">
                <ReceiptField
                  currentSelectionMode={selectionMode}
                  selectionMode={SelectionMode.Brand}
                  selectedItems={inputs[SelectionMode.Brand]}
                  handleSelectionModeChange={handleSelectionModeChange}
                  value={formatLabeledText(SelectionMode.Brand)}
                  label="1. Brand name and address"
                />
                <ReceiptField
                  currentSelectionMode={selectionMode}
                  selectionMode={SelectionMode.ReceiptNumber}
                  selectedItems={inputs[SelectionMode.ReceiptNumber]}
                  handleSelectionModeChange={handleSelectionModeChange}
                  value={formatLabeledText(SelectionMode.ReceiptNumber)}
                  label="2. Receipt/transaction number"
                />
                <ReceiptField
                  currentSelectionMode={selectionMode}
                  selectionMode={SelectionMode.DateTime}
                  selectedItems={inputs[SelectionMode.DateTime]}
                  handleSelectionModeChange={handleSelectionModeChange}
                  value={formatLabeledText(SelectionMode.DateTime)}
                  label="3. Date & time of transaction"
                />
                <ReceiptField
                  currentSelectionMode={selectionMode}
                  selectionMode={SelectionMode.Amount}
                  selectedItems={inputs[SelectionMode.Amount]}
                  handleSelectionModeChange={handleSelectionModeChange}
                  value={formatLabeledText(SelectionMode.Amount)}
                  label="4. Net amount paid"
                />
              </div>
            </div>
          </div>
        )}

        <div className="col-xl-8 col-12 pl-0">
          {!IsEmpty(imageUrl) && !IsEmpty(imageSize) && (
            <Tooltip
              title={pointedText}
              open={!IsEmpty(pointedText) && !IsEmpty(selectionMode)}
              placement="top"
            >
              <div
                className="d-flex flex-column justify-content-center align-items-center w-100 px-3 pt-4 pb-4 position-relative"
                style={{ borderRadius: '15px', border: `2px solid ${Colours.LightGray}` }}
              >
                <div className="position-relative" style={{ width: '95%', aspectRatio: (imageSize.width / imageSize.height) }}>
                  <img
                    src={sampleImage}
                    style={{ width: '100%', height: '100%' }}
                    className="position-absolute"
                    alt="receipt"
                  />
                  {!IsEmpty(extractedData) && !IsEmpty(selectionMode) && extractedData.map((item) => (
                    <BoundingBox
                      key={item.line}
                      item={item}
                      onClick={() => handlePolygonClick(item.line)}
                      selectionMode={selectionMode}
                      inputs={inputs}
                      onMouseOver={() => setPointedText(item.text)}
                      onMouseOut={() => setPointedText('')}
                      imageSize={imageSize}
                    />
                  ))}
                </div>
              </div>
            </Tooltip>
          )}

          <div className="d-flex mt-2">
            <Tooltip
              title={
                !IsEmpty(imageUrl) && IsEmpty(extractedData)
                  ? 'Image processing in-progress. You can replace the current receipt image when it has ben processed'
                  : 'Upload'
              }
            >
              <div className="mr-2">
                <IconButton
                  disabled={!IsEmpty(imageUrl) && IsEmpty(extractedData)}
                  onClick={handleImageUpload}
                  className="shadow-sm bg-white"
                >
                  <CloudUploadIcon />
                </IconButton>
              </div>
            </Tooltip>
            {!IsEmpty(imageUrl) && (
              <Tooltip title="View">
                <div className="mr-2">
                  <IconButton onClick={() => setImageViewerOpen(true)} className="shadow-sm bg-white">
                    <VisibilityIcon />
                  </IconButton>
                </div>
              </Tooltip>
            )}
          </div>
        </div>

      </div>

      {imageViewerOpen && (
        <Lightbox
          animationDisabled
          mainSrc={imageUrl}
          onCloseRequest={() => setImageViewerOpen(false)}
        />
      )}
    </div>
  );
};

export default Receipt;
